import { findSuggestion, getUpdatedContentArea } from './findSuggestion';
import { RANGE_END_ID, RANGE_START_ID } from '../../../shared/constants';
import { ContentArea } from '../types';

interface IMakeSuggestionElProps {
  content: string;
  patternContent?: string;
  patternList?: string[];
  id?: string;
  classNames?: string[];
  tagName: 'mark' | 'a';
}

/**
 * @interface IMakeSuggestionElProps
 * @property {string} content - The content in which to look for the pattern.
 * @property {string} patternContent - A single pattern used to generate a suggestion that is most similar to the original pattern (case-sensitive matching).
 * @property {string[]} patternList - An array of patterns used to generate exact matches (case-insensitive matching, uses word boundaries for each word).
 * @property {string} id - The ID assigned to the element highlighting the suggestion in the HTML content.
 * @property {string} classNames - The class names added to the HTML element highlighting the suggestion.
 * @property {string} tagName - The tag name of the element highlighting the suggestion.
 * @returns {ContentArea | ContentArea[] | null} - The content area(s) where the suggestion is highlighted, or null if no match is found.
 */
const getContentAreasFromPattern = ({
  content,
  patternContent,
  patternList,
  id,
  classNames,
  tagName,
}: IMakeSuggestionElProps): ContentArea | ContentArea[] | null => {
  const suggestionPattern = findSuggestion(
    tagName,
    content,
    patternContent,
    patternList,
  );

  if (!suggestionPattern) {
    return null;
  }

  const contentAreas = suggestionPattern
    .map((p) => {
      const contentBeforePattern = content.substring(0, p.position.start);
      const contentAfterPattern = content.substring(
        p.position.end,
        content.length,
      );

      // use void elememt to mark start and end of range
      const newContent = `${contentBeforePattern}${`<br id='${RANGE_START_ID}'>${p.content}<br id='${RANGE_END_ID}'>`}${contentAfterPattern}`;
      const updatedContentArea = getUpdatedContentArea(newContent);

      if (updatedContentArea) {
        return {
          id: id || undefined,
          ...updatedContentArea,
          classNames,
          tagName,
        };
      }

      return null;
    })
    .filter((a) => a !== null);

  return patternList?.length
    ? (contentAreas as ContentArea[])
    : (contentAreas[0] as ContentArea) ?? null;
};

export default getContentAreasFromPattern;
